import React, { useEffect, useState, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { Tag, Typography, Button, Modal, Card, Divider, message, Avatar, List, Descriptions, Input, Space, Col, Select, Image, Pagination } from "antd";
import DataTable from "../../component/ui/DataTable";
import Breadcrumbs from "../../component/Breadcrumbs";
import { EyeOutlined, CheckCircleOutlined, CloseCircleOutlined, ReloadOutlined } from "@ant-design/icons";
import { get, put } from "../../service/base";
import EmptyState from "../../util/EmptyState";
import { OutlineButton, ResetButton } from "../../component/ui/ActionButtons";
import TableImage from "../../component/ui/TableImage";
import { getImageUrl } from "../../component/SafeImage";
import TableActions from "../../component/ui/TableActions";
import { FilterBar } from "../../component/ui/FilterBar";
import SearchInput from "../../component/ui/SearchInput";
import DateRangePicker from "../../component/ui/DateRangePicker";
import QuickFilter from "../../component/ui/QuickFilter";
import dayjs from "dayjs";
import { getOrderRowClass } from "../../util/statusHighlight";
import { useAdminTable } from "../hooks/useAdminTable";

const { Title, Text } = Typography;
const { Option } = Select;

const AdminReturns = () => {
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [adminComment, setAdminComment] = useState("");
    const [failedImages, setFailedImages] = useState({});
    const [revealedDetails, setRevealedDetails] = useState(null);
    const [revealLoading, setRevealLoading] = useState(false);
    const [utrInput, setUtrInput] = useState("");

    // New state variables for the confirmation popup
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const [confirmActionStatus, setConfirmActionStatus] = useState("");
    const [confirmNote, setConfirmNote] = useState("");

    const fetchRequests = async (params) => {
        const queryParams = {
            limit: params.limit,
            page: params.page,
            type: "return",   // ← Only fetch return requests (not cancellations)
        };
        if (params.search) queryParams.search = params.search;

        const queryString = new URLSearchParams(queryParams).toString();
        const res = await get(`/Orders/returns/admin-all?${queryString}`);
        return { data: res?.data || [], total: res?.total || 0 };
    };

    const {
        data: requests,
        setData: setRequests,
        total: totalRequests,
        loading,
        params,
        setParams,
        handleSearch,
        refresh
    } = useAdminTable(fetchRequests, "admin_returns", { page: 1, limit: 10, dateRange: null });

    const location = useLocation();

    const filteredRequests = useMemo(() => {
        let filtered = requests;

        if (params.dateRange && params.dateRange[0] && params.dateRange[1]) {
            const start = params.dateRange[0].startOf('day');
            const end = params.dateRange[1].endOf('day');
            filtered = filtered.filter(r => {
                const created = dayjs(r.createdAt);
                return created.isAfter(start) && created.isBefore(end);
            });
        }

        return filtered;
    }, [requests, params.dateRange]);

    useEffect(() => {
        if (location.state?.highlightOrderId) {
            setParams(prev => ({ ...prev, search: location.state.highlightOrderId, page: 1 }));
        }
    }, [location.state?.highlightOrderId]);

    useEffect(() => {
        if (location.state?.highlightOrderId && filteredRequests.length > 0) {
            const req = filteredRequests.find(x =>
                (x.orderId && (x.orderId._id === location.state.highlightOrderId || x.orderId.orderId === location.state.highlightOrderId || x.orderId === location.state.highlightOrderId)) ||
                x._id === location.state.highlightOrderId
            );
            if (req) {
                setSelectedRequest(req);
                setAdminComment(req.sellerComment || "");
                setIsModalOpen(true);
                if (req.refundDetails) {
                    handleRevealDetails(req);
                }
            }
        }
    }, [location.state, filteredRequests]);

    const handleViewDetails = (req) => {
        setSelectedRequest(req);
        setAdminComment(req.sellerComment || "");
        setFailedImages({});
        setRevealedDetails(null);
        setUtrInput("");
        setIsModalOpen(true);
        if (req.refundDetails) {
            handleRevealDetails(req);
        }
    };

    const handleUpdateStatus = async (status, commentStr = "") => {
        if (!selectedRequest) return;
        try {
            setActionLoading(true);
            await put(`/Orders/returns/${selectedRequest._id}/status`, {
                status,
                sellerComment: commentStr ? commentStr + " (Admin Action)" : (adminComment ? adminComment + " (Admin Action)" : "")
            });
            message.success(`Return request status updated to: ${status.replace(/_/g, ' ')}`);
            setIsModalOpen(false);
            refresh();
        } catch (error) {
            message.error(error.message || "Failed to update status");
        } finally {
            setActionLoading(false);
        }
    };

    const handleProcessRefund = async () => {
        if (!selectedRequest) return;
        
        const methodUpper = (selectedRequest.refundMethod || "WALLET").toUpperCase();
        const payload = {
            stage: "completed",
            refundMethod: methodUpper
        };

        if (['UPI', 'BANK', 'MANUAL_TRANSFER'].includes(methodUpper)) {
            if (!utrInput?.trim()) {
                message.warning("Please enter a UTR / Transaction Reference ID to finalize refund.");
                return;
            }
            payload.refundTransactionId = utrInput.trim();
        }

        try {
            setActionLoading(true);
            await put(`/Orders/returns/admin/${selectedRequest._id}/refund`, payload);
            message.success(`Refund processed successfully via ${methodUpper}.`);
            setIsModalOpen(false);
            refresh();
        } catch (error) {
            message.error(error.message || "Failed to process refund");
        } finally {
            setActionLoading(false);
        }
    };

    const handleRevealDetails = async (requestObj = null) => {
        const targetReq = requestObj || selectedRequest;
        if (!targetReq) return;
        try {
            setRevealLoading(true);
            const res = await get(`/Orders/returns/admin/${targetReq._id}/refund-details`);
            if (res && res.success) {
                setRevealedDetails(res.data);
            } else if (res && res.data) {
                setRevealedDetails(res.data);
            } else {
                message.error("Failed to load details");
            }
        } catch (err) {
            message.error(err.message || "Failed to reveal refund details");
        } finally {
            setRevealLoading(false);
        }
    };

    const confirmAction = (status) => {
        const isApprove = status.includes('APPROVED');
        setConfirmActionStatus(status);
        setConfirmNote(isApprove ? "Approved after admin review." : "");
        setIsConfirmOpen(true);
    };

    const handleConfirmOk = async () => {
        const isApprove = confirmActionStatus.includes('APPROVED');
        if (!isApprove && !confirmNote.trim()) {
            message.error("Rejection reason is required.");
            return;
        }
        await handleUpdateStatus(confirmActionStatus, confirmNote);
        setIsConfirmOpen(false);
    };

    // ─── Helper Functions ───────────────────────────────────────────────────────
    const getDisplayItems = (record) => {
        if (record?.returnItems?.length) return record.returnItems;
        if (record?.items?.length) return record.items;
        if (record?.order?.items?.length) return record.order.items;
        return [];
    };

    const flattenedRequests = useMemo(() => {
        const flatList = [];
        filteredRequests.forEach(request => {
            const items = getDisplayItems(request);
            if (items && items.length > 0) {
                items.forEach((item, index) => {
                    flatList.push({
                        ...request,
                        _item: item,
                        _itemIndex: index,
                        _uniqueKey: `${request._id}_${index}`
                    });
                });
            } else {
                flatList.push({
                    ...request,
                    _item: null,
                    _itemIndex: 0,
                    _uniqueKey: `${request._id}_0`
                });
            }
        });
        return flatList;
    }, [filteredRequests]);

    const getProductName = (item) =>
        item?.productName || item?.name || item?.product?.name || item?.productId?.name || "—";

    const getProductId = (item) => {
        const prod = item?.product || item?.productId;
        if (prod && typeof prod === 'object' && prod.productId) {
            return prod.productId;
        }
        const id = prod?._id || prod;
        return id ? String(id).slice(-8).toUpperCase() : "—";
    };

    const getVariant = (item, record) => {
        // 1. Direct selectedOptions on returnItem
        let opts = item?.selectedOptions;

        // 2. Match via order.items using product reference
        let orderItem = null;
        if (record?.order?.items?.length) {
            // product may be a populated object { _id, name } or a raw ObjectId string
            const pId = String(
                item?.productId?._id || item?.productId ||
                item?.product?._id || item?.product || ''
            );

            if (pId) {
                orderItem = record.order.items.find(oi => {
                    const oiId = String(oi?.product?._id || oi?.product || '');
                    return oiId === pId;
                });
                if (orderItem && !opts) opts = orderItem.selectedOptions;
            }
        }

        if (opts && typeof opts === 'object') {
            const entries = Object.entries(opts).filter(([, v]) => v);
            if (entries.length) {
                return entries.map(([k, v]) => `${k}: ${v}`).join(', ');
            }
        }

        // 3. Fallback: Match item price to product variants
        const prod = item?.productId || item?.product;
        if (prod && typeof prod === 'object') {
            const targetPrice = item.price ?? orderItem?.price ?? 0;

            // 3a. Search legacy variants
            if (prod.variants?.length) {
                const matchedVar = prod.variants.find(v => (v.price || 0) === targetPrice);
                if (matchedVar) {
                    let optionName = "";
                    if (prod.options?.length) {
                        const optGroup = prod.options.find(o =>
                            o.variants?.some(ov =>
                                String(ov._id || ov) === String(matchedVar._id) ||
                                ov.value === matchedVar.value
                            )
                        );
                        if (optGroup) optionName = optGroup.name;
                    }
                    if (!optionName) {
                        optionName = prod.variantType || "Variant";
                    }
                    return `${optionName}: ${matchedVar.value}`;
                }
            }

            // 3b. Search nested options variants
            if (prod.options?.length) {
                for (const optGroup of prod.options) {
                    if (optGroup.variants?.length) {
                        const matchedVar = optGroup.variants.find(v => (v.price || 0) === targetPrice);
                        if (matchedVar) {
                            const optionName = optGroup.name || prod.variantType || "Variant";
                            return `${optionName}: ${matchedVar.value}`;
                        }
                    }
                }
            }
        }

        return "—";
    };


    const getQty = (item) => item?.returnQty ?? item?.quantity ?? item?.orderedQty ?? 0;

    const formatINR = (val) =>
        val != null ? `₹${Number(val).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "—";

    const STATUS_COLORS = {
        REQUESTED: 'orange', RETURN_REQUESTED: 'orange',
        APPROVED: 'blue', RETURN_APPROVED_BY_ADMIN: 'blue',
        RETURN_PICKUP_PENDING: 'blue', RETURN_IN_TRANSIT: 'blue',
        RETURN_PICKUP_ASSIGNED: 'cyan', RETURN_PICKUP_ACCEPTED: 'cyan',
        RETURN_DELIVERED_TO_SELLER: 'cyan',
        PICKED_UP: 'purple', RETURN_PICKED_UP: 'purple',
        REJECTED: 'red', RETURN_REJECTED_BY_ADMIN: 'red', RETURN_PICKUP_REJECTED: 'red',
        RETURN_COMPLETED: 'green', REFUND_COMPLETED: 'green',
        WAITING_FOR_ADMIN_REFUND: 'orange', REFUND_PENDING: 'orange', REFUND_PROCESSING: 'orange',
    };

    const formatStatus = (s) => {
        if (!s) return "—";
        return s.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
    };

    const getApprovedBy = (record) => {
        const timeline = [...(record?.returnTimeline || [])].reverse();
        const recStatus = (record?.status || '').toUpperCase();
        const isRejected = recStatus.includes('REJECTED');

        // 1. Find admin who took an actionable step with a populated name
        const adminEntry = timeline.find(t => {
            const role = (t?.actorRole || '').toLowerCase();
            const tStatus = (t?.status || '').toUpperCase();
            const isAdmin = role === 'admin' || role === 'superadmin';
            const isActionable = (
                tStatus.includes('APPROVED') ||
                tStatus === 'REFUND_COMPLETED' ||
                tStatus === 'RETURN_COMPLETED' ||
                tStatus.includes('REJECTED')
            );
            return isAdmin && isActionable && t?.actorId?.firstName;
        });
        if (adminEntry?.actorId?.firstName) {
            return `${adminEntry.actorId.firstName} ${adminEntry.actorId.lastName || ''}`.trim();
        }

        // 2. Any admin timeline entry with a populated name
        const anyAdmin = timeline.find(t => {
            const role = (t?.actorRole || '').toLowerCase();
            return (role === 'admin' || role === 'superadmin') && t?.actorId?.firstName;
        });
        if (anyAdmin?.actorId?.firstName) {
            return `${anyAdmin.actorId.firstName} ${anyAdmin.actorId.lastName || ''}`.trim();
        }

        // 3. For rejections: use rejectedBy field (stored as separate ObjectId ref)
        if (isRejected && record?.rejectedBy?.firstName) {
            return `${record.rejectedBy.firstName} ${record.rejectedBy.lastName || ''}`.trim();
        }

        // 3.5 Fallback: Try to find admin name from order.statusHistory
        if (record?.order?.statusHistory) {
            const history = [...record.order.statusHistory].reverse();
            const adminHist = history.find(h => {
                const comment = (h?.comment || '').toUpperCase();
                const hasAdminKeyword = (
                    comment.includes('RETURN_APPROVED_BY_ADMIN') ||
                    comment.includes('APPROVED_BY_ADMIN') ||
                    comment.includes('RETURN_REJECTED_BY_ADMIN') ||
                    comment.includes('REJECTED_BY_ADMIN') ||
                    comment.includes('REFUND STAGE') ||
                    comment.includes('REFUND COMPLETED')
                );
                return hasAdminKeyword && h?.updatedBy?.firstName;
            });
            if (adminHist?.updatedBy?.firstName) {
                return `${adminHist.updatedBy.firstName} ${adminHist.updatedBy.lastName || ''}`.trim();
            }
        }

        // 4. Timeline has admin role entry but actorId was null (pre-fix records)
        const hasAdminNote = timeline.some(t => {
            const role = (t?.actorRole || '').toLowerCase();
            return role === 'admin' || role === 'superadmin';
        });
        if (hasAdminNote) return 'Admin';

        // 5. Infer from adminComment / sellerComment "(Admin Action)" tag
        //    When admin used handleUpdateStatus(), sellerComment was saved with "(Admin Action)" suffix
        const adminComment = record?.adminComment || '';
        const sellerComment = record?.sellerComment || '';
        if (adminComment.includes('Admin') || sellerComment.includes('Admin Action')) {
            return 'Admin';
        }

        // 6. STATUS-BASED INFERENCE for old records with NO timeline entries at all:
        //    If the status is past "return_requested/requested", admin must have approved it
        const statusesThatRequireAdminApproval = [
            'RETURN_APPROVED_BY_ADMIN', 'APPROVED_BY_ADMIN',
            'RETURN_PICKUP_PENDING', 'RETURN_PICKUP_ASSIGNED',
            'RETURN_STAFF_ACCEPTED', 'RETURN_STAFF_ARRIVED_AT_CUSTOMER',
            'RETURN_PICKED_UP', 'RETURN_IN_TRANSIT', 'RETURN_IN_TRANSIT_TO_SHOP',
            'RETURN_ARRIVED_AT_SHOP', 'RETURN_DELIVERED_TO_SELLER',
            'RETURN_COMPLETED', 'WAITING_FOR_ADMIN_REFUND',
            'REFUND_PENDING', 'REFUND_PROCESSING', 'REFUND_COMPLETED',
            'RETURN_REJECTED_BY_ADMIN', 'REJECTED_BY_ADMIN',
            'RETURN_PICKUP_REJECTED',
        ];
        if (statusesThatRequireAdminApproval.includes(recStatus)) {
            return 'Admin';
        }

        return '—';
    };


    const getRefundTransaction = (record) =>
        record?.refundTransaction?.referenceId ||
        record?.refundTransaction?._id ||
        record?.refundTransactionId ||
        "—";

    const getRemarks = (record) => {
        // Priority: adminComment > sellerComment > rejectionReason > failureReason > latest timeline note
        if (record?.adminComment) return record.adminComment;
        if (record?.sellerComment) return record.sellerComment;
        if (record?.rejectionReason) return record.rejectionReason;
        if (record?.failureReason) return record.failureReason;

        // Fallback: get last meaningful note from returnTimeline
        const notes = (record?.returnTimeline || [])
            .filter(t => t?.note && t.note.trim())
            .map(t => t.note);
        if (notes.length) return notes[notes.length - 1];

        return "—";
    };

    const getRefundReason = (record) => {
        const items = getDisplayItems(record);
        const reasons = items.map(i => i?.returnReason).filter(Boolean);
        if (reasons.length) return reasons.join('; ');
        return record?.sellerComment || "—";
    };

    const getItemCouponDiscount = (record) => {
        if (!record?._item) return 0;
        if (record._item.couponDiscount !== undefined && record._item.couponDiscount !== null) {
            return record._item.couponDiscount;
        }
        const itemQty = getQty(record._item);
        const itemGross = (record._item.price || 0) * itemQty;
        const orderItems = record.order?.items || [];
        const orderSubtotal = orderItems.reduce((acc, oi) => acc + ((oi.price || 0) * (oi.quantity || 0)), 0);
        const orderCouponDiscount = Number(record.order?.couponDiscount) || 0;

        if (orderSubtotal <= 0 || orderCouponDiscount <= 0) return 0;
        const discount = Math.round(((itemGross / orderSubtotal) * orderCouponDiscount) * 100) / 100;
        return Math.min(discount, itemGross);
    };

    const getItemRefundAmount = (record) => {
        if (!record?._item) return 0;
        if (record._item.netRefundAmount !== undefined && record._item.netRefundAmount !== null) {
            return record._item.netRefundAmount;
        }
        const itemQty = getQty(record._item);
        const itemGross = (record._item.price || 0) * itemQty;
        const itemDiscount = getItemCouponDiscount(record);
        return Math.max(0, Math.round((itemGross - itemDiscount) * 100) / 100);
    };

    // ─── Columns ────────────────────────────────────────────────────────────────
    const columns = [
        {
            title: "Refund ID",
            dataIndex: "returnId",
            key: "returnId",
            width: 120,
            render: (id) => <span style={{ color: '#334155', fontSize: 13, fontWeight: 600 }}>{id || "—"}</span>
        },
        {
            title: "Order ID",
            key: "orderId",
            width: 120,
            render: (_, r) => <span style={{ color: '#334155', fontSize: 13, fontWeight: 600 }}>{r.order?.orderId || "—"}</span>
        },
        {
            title: "Order Date",
            key: "orderDate",
            width: 155,
            render: (_, r) => <span style={{ color: '#334155', fontSize: 13, fontWeight: 600 }}>{r.order?.createdAt ? dayjs(r.order.createdAt).format('DD MMM YYYY') : "—"}</span>
        },
        {
            title: "Refund Request Date",
            dataIndex: "createdAt",
            key: "createdAt",
            width: 170,
            render: (v) => <span style={{ color: '#334155', fontSize: 13, fontWeight: 600 }}>{v ? dayjs(v).format('DD MMM YYYY') : "—"}</span>
        },
        {
            title: "Refund Processed Date",
            key: "refundProcessedDate",
            width: 175,
            render: (_, r) => {
                const completedStatuses = ['refund_completed', 'return_completed'];
                if (!completedStatuses.includes((r.status || '').toLowerCase())) return <span style={{ color: '#9ca3af', fontSize: 13, fontWeight: 600 }}>—</span>;
                return <span style={{ color: '#334155', fontSize: 13, fontWeight: 600 }}>{r.refundCompletedAt ? dayjs(r.refundCompletedAt).format('DD MMM YYYY') : dayjs(r.updatedAt).format('DD MMM YYYY')}</span>;
            }
        },
        {
            title: "Seller / Shop Name",
            key: "shopName",
            width: 160,
            render: (_, r) => {
                const sellerName = r.shop?.createdBy
                    ? `${r.shop.createdBy.firstName} ${r.shop.createdBy.lastName || ''}`.trim()
                    : '—';
                return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        <span style={{ color: '#334155', fontSize: 13, fontWeight: 600 }}>{r.shop?.name || "—"}</span>
                        <span style={{ color: '#334155', fontSize: 13, fontWeight: 600 }}>👤 {sellerName}</span>
                    </div>
                );
            }
        },
        {
            title: "Product Name",
            key: "productName",
            width: 200,
            render: (_, r) => <span style={{ color: '#334155', fontSize: 13, fontWeight: 600 }}>{getProductName(r._item)}</span>
        },
        {
            title: "Product ID",
            key: "productId",
            width: 120,
            render: (_, r) => <span style={{ color: '#334155', fontSize: 13, fontWeight: 600 }}>{getProductId(r._item)}</span>
        },
        {
            title: "Variant",
            key: "variant",
            width: 160,
            render: (_, r) => <span style={{ color: '#334155', fontSize: 13, fontWeight: 600 }}>{getVariant(r._item, r)}</span>
        },
        {
            title: "Quantity",
            key: "quantity",
            width: 50,
            align: 'center',
            render: (_, r) => <span style={{ color: '#334155', fontSize: 13, fontWeight: 600 }}>{getQty(r._item)}</span>
        },
        {
            title: "Unit Price",
            key: "unitPrice",
            width: 110,
            align: 'right',
            render: (_, r) => <span style={{ color: '#334155', fontSize: 13, fontWeight: 600 }}>{formatINR(r._item?.price)}</span>
        },
        {
            title: "Product Amount",
            key: "productAmount",
            width: 130,
            align: 'right',
            render: (_, r) => {
                if (!r._item) return <span style={{ color: '#9ca3af', fontSize: 13, fontWeight: 600 }}>—</span>;
                return <span style={{ color: '#334155', fontSize: 13, fontWeight: 600 }}>{formatINR((r._item.price || 0) * getQty(r._item))}</span>;
            }
        },
        {
            title: "Discount / Coupon",
            key: "discount",
            width: 140,
            align: 'right',
            render: (_, r) => <span style={{ color: '#334155', fontSize: 13, fontWeight: 600 }}>{formatINR(getItemCouponDiscount(r))}</span>
        },
        {
            title: "Delivery Fee",
            key: "deliveryFee",
            width: 120,
            align: 'right',
            render: (_, r) => <span style={{ color: '#334155', fontSize: 13, fontWeight: 600 }}>{formatINR(r.order?.deliveryCharge ?? 0)}</span>
        },
        {
            title: "Refund Amount",
            key: "refundAmount",
            width: 140,
            align: 'right',
            render: (_, r) => {
                const itemRefund = getItemRefundAmount(r);
                const items = getDisplayItems(r);
                const showRequestTotal = items.length > 1;

                return (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                        <span style={{ fontWeight: 600, color: '#334155', fontSize: 13 }}>
                            {formatINR(itemRefund)}
                        </span>
                        {showRequestTotal && (
                            <span style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
                                Total: {formatINR(r.refundAmount)}
                            </span>
                        )}
                    </div>
                );
            }
        },
        {
            title: "Refund Reason",
            key: "refundReason",
            width: 180,
            render: (_, r) => <span style={{ color: '#334155', fontSize: 13, fontWeight: 600 }}>{r._item?.returnReason || r.sellerComment || "—"}</span>
        },
        {
            title: "Refund Type",
            key: "refundType",
            width: 110,
            render: (_, r) => r.type ? <Tag style={{ borderRadius: 6, textTransform: 'capitalize', fontSize: 12 }}>{r.type}</Tag> : <span style={{ color: '#334155', fontSize: 13, fontWeight: 600 }}>—</span>
        },
        {
            title: "Payment Method",
            key: "paymentMethod",
            width: 140,
            render: (_, r) => <span style={{ color: '#334155', fontSize: 13, fontWeight: 600 }}>{r.order?.paymentMethod || "—"}</span>
        },
        {
            title: "Refund Method",
            key: "refundMethod",
            width: 140,
            render: (_, r) => {
                if (r.refundMethod) {
                    return (
                        <Tag color={r.refundMethod === 'WALLET' ? 'purple' : 'blue'} style={{ borderRadius: 6, fontSize: 12 }}>
                            {r.refundMethod.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ')}
                        </Tag>
                    );
                }

                const status = (r.status || "").toUpperCase();

                const completedStatuses = [
                    "REFUND_COMPLETED",
                    "RETURN_COMPLETED"
                ];

                if (!completedStatuses.includes(status)) {
                    return <span style={{ color: "#9ca3af", fontSize: 13, fontWeight: 600 }}>—</span>;
                }

                if (
                    r.refundTransaction?._id ||
                    r.refundTransaction?.referenceId ||
                    r.refundTransactionId
                ) {
                    return (
                        <Tag color="purple" style={{ borderRadius: 6, fontSize: 12 }}>
                            Wallet Credit
                        </Tag>
                    );
                }

                const method = r.order?.paymentMethod?.toUpperCase();

                if (!method) {
                    return <span style={{ color: '#334155', fontSize: 13, fontWeight: 600 }}>—</span>;
                }

                if (method === "UPI") {
                    return <span style={{ color: '#334155', fontSize: 13, fontWeight: 600 }}>Original UPI</span>;
                }

                if (
                    method === "NETBANKING" ||
                    method === "NET_BANKING" ||
                    method === "ONLINE"
                ) {
                    return <span style={{ color: '#334155', fontSize: 13, fontWeight: 600 }}>Original Source</span>;
                }

                if (method === "CARD") {
                    return <span style={{ color: '#334155', fontSize: 13, fontWeight: 600 }}>Original CARD</span>;
                }

                if (
                    method === "COD" ||
                    method === "CASH_ON_DELIVERY"
                ) {
                    return <span style={{ color: '#334155', fontSize: 13, fontWeight: 600 }}>—</span>;
                }

                return <span style={{ color: '#334155', fontSize: 13, fontWeight: 600 }}>{`Original ${method}`}</span>;
            }
        },
        {
            title: "Refund Transaction",
            key: "refundTransaction",
            width: 170,
            render: (_, r) => {
                const status = (r.status || "").toUpperCase();

                const completedStatuses = [
                    "REFUND_COMPLETED",
                    "RETURN_COMPLETED"
                ];

                // Refund complete ஆகவில்லை
                if (!completedStatuses.includes(status)) {
                    return <span style={{ color: "#9ca3af", fontSize: 13, fontWeight: 600 }}>—</span>;
                }

                // Actual transaction ID available
                const transaction =
                    r.refundTransaction?.referenceId ||
                    r.refundTransaction?.transactionId ||
                    r.refundTransaction?._id ||
                    r.refundTransactionId;

                if (transaction) {
                    return (
                        <span
                            className="monospace-text"
                            style={{
                                fontFamily: "monospace",
                                fontSize: 11,
                                color: "#1d4ed8",
                                fontWeight: 600
                            }}
                        >
                            {transaction}
                        </span>
                    );
                }

                // Online payment refund completed but transaction ID
                // not returned by API
                const method = r.order?.paymentMethod?.toUpperCase();

                if (
                    method &&
                    method !== "COD" &&
                    method !== "CASH_ON_DELIVERY"
                ) {
                    return (
                        <span
                            style={{
                                fontSize: 11,
                                color: "#334155",
                                fontWeight: 600
                            }}
                        >
                            Processed by Gateway
                        </span>
                    );
                }

                return <span style={{ color: "#9ca3af", fontSize: 13, fontWeight: 600 }}>—</span>;
            }
        },
        {
            title: "Refund Status",
            dataIndex: "status",
            key: "status",
            width: 180,
            render: (s) => {
                const key = (s || '').toUpperCase();
                return <Tag color={STATUS_COLORS[key] || 'default'} style={{ borderRadius: 6, fontWeight: 600 }}>{formatStatus(s)}</Tag>;
            }
        },
        {
            title: "Approved By",
            key: "approvedBy",
            width: 130,
            render: (_, record) => <span style={{ color: '#334155', fontSize: 13, fontWeight: 600 }}>{getApprovedBy(record)}</span>
        },
        {
            title: "Remarks",
            key: "remarks",
            width: 200,
            render: (_, record) => <span style={{ color: '#334155', fontSize: 13, fontWeight: 600 }}>{getRemarks(record)}</span>
        },
        {
            title: 'Action',
            key: 'action',
            width: 90,
            align: 'center',
            render: (_, record) => (
                <Button
                    type="default"
                    size="small"
                    onClick={() => handleViewDetails(record)}
                    style={{ borderRadius: 4, fontWeight: 600, color: '#003a8c', borderColor: '#d9d9d9' }}
                >
                    View
                </Button>
            ),
        }
    ];

    const processedColumns = columns.map(col => {
        const nowrapTitles = [
            "Refund ID", "Order ID", "Order Date", "Refund Request Date", "Refund Processed Date",
            "Product ID", "Quantity", "Unit Price", "Product Amount", "Discount / Coupon",
            "Delivery Fee", "Refund Amount", "Refund Type", "Payment Method", "Refund Method",
            "Refund Transaction", "Refund Status", "Approved By"
        ];
        if (nowrapTitles.includes(col.title)) {
            return {
                ...col,
                className: `${col.className || ''} nowrap-column`.trim()
            };
        }
        return col;
    });

    const fetchFilteredReturns = async () => {
        return flattenedRequests;
    };

    const handleReset = () => {
        setParams({ page: 1, limit: 10, search: "", dateRange: null });
    };

    return (
        <div className="product-option-container pcu-padding-24">
            <style>{`
                .custom-table .ant-table-thead > tr > th {
                    font-size: 13px !important;
                    font-weight: 700 !important;
                    color: #1e293b !important;
                    text-transform: capitalize !important;
                    letter-spacing: normal !important;
                    opacity: 1 !important;
                }
                .custom-table .ant-table-tbody > tr > td {
                    font-family: var(--font-family-primary, "DM Sans", sans-serif) !important;
                    font-size: 13px !important;
                }
                .custom-table .ant-table-tbody > tr > td * {
                    font-family: var(--font-family-primary, "DM Sans", sans-serif) !important;
                }
                .custom-table .ant-table-tbody > tr > td .monospace-text {
                    font-family: monospace !important;
                }
                .custom-table .nowrap-column {
                    white-space: nowrap !important;
                }
            `}</style>
            <div className="pcu-margin-bottom-16">
                <Breadcrumbs />
            </div>

            <div className="main-content-card pcu-bg-white pcu-radius-16 pcu-shadow-sm pcu-overflow-hidden">
                <div className="pcu-padding-x-24 pcu-padding-top-24 pcu-padding-bottom-16 pcu-border-bottom">
                    <div className="pcu-flex pcu-justify-between pcu-items-start">
                        <h1 className="pcu-font-22 pcu-font-700 pcu-color-title pcu-margin-0">
                            Global Return & Refund Audit
                        </h1>
                        <div className="pcu-flex pcu-gap-12 pcu-items-center">
                            <TableActions
                                onFetchFullData={fetchFilteredReturns}
                                columns={processedColumns}
                                fileName="return_audits"
                                pageName="Return Audits"
                            />
                            <ResetButton
                                icon={<ReloadOutlined />}
                                onClick={() => {
                                    handleReset();
                                }}
                                text="Refresh"
                            />
                        </div>
                    </div>
                </div>

                <div className="pcu-padding-24">
                    <FilterBar>
                        <Col xs={24} md={8}>
                            <SearchInput
                                label="Search Returns"
                                placeholder="Search by ID, Order ID, User, Shop..."
                                value={params.search}
                                onChange={(val) => handleSearch(val)}
                            />
                        </Col>
                        <Col xs={24} md={8}>
                            <QuickFilter
                                value={params.dateRange ? "custom" : undefined}
                                onChange={(key, range) => {
                                    setParams(prev => ({ ...prev, dateRange: range, page: 1 }));
                                }}
                            />
                        </Col>
                        <Col xs={24} md={8}>
                            <DateRangePicker
                                label="Custom Date Range"
                                value={params.dateRange}
                                onChange={(val) => {
                                    setParams(prev => ({ ...prev, dateRange: val, page: 1 }));
                                }}
                            />
                        </Col>
                    </FilterBar>

                    <div className="pcu-border-top">
                        <DataTable
                            columns={processedColumns}
                            data={flattenedRequests}
                            rowKey="_uniqueKey"
                            loading={loading}
                            scroll={{ x: 3600 }}
                            pagination={false}
                            rowClassName={() => ""}
                            locale={{
                                emptyText: (
                                    <EmptyState
                                        title="No Return Audits"
                                        description="No return requests found in the system."
                                        gradient="orange"
                                        showAction={false}
                                        icon="inbox"
                                    />
                                )
                            }}
                        />
                    </div>

                    <div style={{ padding: '16px', display: 'flex', justifyContent: 'flex-end', borderTop: '1px solid #f0f0f0' }}>
                        <Pagination
                            current={params.page}
                            pageSize={params.limit}
                            total={totalRequests}
                            showSizeChanger
                            onShowSizeChange={(c, s) => setParams(prev => ({ ...prev, limit: s, page: 1 }))}
                            onChange={(p) => setParams(prev => ({ ...prev, page: p }))}
                            showTotal={(total) => `Total ${total} requests`}
                        />
                    </div>
                </div>
            </div>

            <Modal
                title={null}
                open={isModalOpen}
                onCancel={() => setIsModalOpen(false)}
                footer={null}
                width={540}
                centered
                styles={{ body: { padding: 0 }, content: { borderRadius: 20, overflow: 'hidden', padding: 0 } }}
            >
                {selectedRequest && (
                    <>
                        {/* Premium Header */}
                        <div style={{
                            background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
                            padding: '20px 24px 16px',
                            position: 'relative',
                            overflow: 'hidden'
                        }}>
                            <div style={{ position: 'absolute', top: -30, right: -30, width: 120, height: 120, borderRadius: '50%', background: 'rgba(255,129,16,0.15)' }} />
                            <div style={{ position: 'absolute', bottom: -20, left: '40%', width: 80, height: 80, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />
                            <div style={{ display: 'flex', alignItems: 'center', gap: 14, position: 'relative' }}>
                                <div style={{ width: 38, height: 38, borderRadius: 10, background: 'rgba(255,129,16,0.2)', border: '1px solid rgba(255,129,16,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>
                                    📦
                                </div>
                                <div>
                                    <div style={{ color: '#fff', fontSize: 18, fontWeight: 700, letterSpacing: '-0.3px' }}>Admin Return Audit</div>
                                    <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: 12, marginTop: 2 }}>Review Order #{selectedRequest.order?.orderId}</div>
                                </div>
                            </div>
                        </div>

                        {/* Body */}
                        <div style={{ padding: '16px', maxHeight: '60vh', overflowY: 'auto', background: '#f8f9fc' }}>
                            <Descriptions bordered size="small" column={1} style={{ background: '#fff', borderRadius: 12, overflow: 'hidden', marginBottom: 16, border: '1px solid #e5e7eb' }}>
                                <Descriptions.Item label="Customer">
                                    <div style={{ fontWeight: 600 }}>{selectedRequest.user?.firstName} {selectedRequest.user?.lastName}</div>
                                    {selectedRequest.user?.phone && <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>📞 {selectedRequest.user.phone}</div>}
                                    {selectedRequest.user?.email && <div style={{ fontSize: 11, color: '#6b7280' }}>✉️ {selectedRequest.user.email}</div>}
                                </Descriptions.Item>
                                <Descriptions.Item label="Seller">
                                    <div style={{ fontWeight: 600 }}>{selectedRequest.shop?.name}</div>
                                    {(selectedRequest.shop?.phone || selectedRequest.shop?.createdBy?.phone) && <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>📞 {selectedRequest.shop?.phone || selectedRequest.shop?.createdBy?.phone}</div>}
                                    {(selectedRequest.shop?.email || selectedRequest.shop?.createdBy?.email) && <div style={{ fontSize: 11, color: '#6b7280' }}>✉️ {selectedRequest.shop?.email || selectedRequest.shop?.createdBy?.email}</div>}
                                </Descriptions.Item>
                                <Descriptions.Item label="Refund Method">
                                    <Tag color={selectedRequest.refundMethod === 'WALLET' ? 'purple' : 'blue'} style={{ borderRadius: 6, fontWeight: 600 }}>
                                        {selectedRequest.refundMethod ? selectedRequest.refundMethod.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ') : "Wallet Credit"}
                                    </Tag>
                                </Descriptions.Item>
                                {selectedRequest.refundDetails && (
                                    <Descriptions.Item label="Refund Destination">
                                        {selectedRequest.refundMethod === 'UPI' && (
                                            <div>
                                                <div>UPI ID: <Text strong copyable={!!revealedDetails?.upiId}>{revealedDetails?.upiId || selectedRequest.refundDetails.upiId}</Text></div>
                                                {!revealedDetails?.upiId && (
                                                    <Button 
                                                        size="small" 
                                                        type="link" 
                                                        onClick={() => handleRevealDetails(selectedRequest)} 
                                                        loading={revealLoading}
                                                        style={{ padding: 0, height: 'auto', marginTop: 4, fontSize: 11 }}
                                                    >
                                                        👁 Reveal Unmasked UPI ID
                                                    </Button>
                                                )}
                                            </div>
                                        )}
                                        {selectedRequest.refundMethod === 'BANK' && (
                                            <div>
                                                <div style={{ fontSize: 12, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                    <div>Bank: <Text strong>{selectedRequest.refundDetails.bankName}</Text></div>
                                                    <div>Holder: <Text strong>{revealedDetails?.accountHolderName || selectedRequest.refundDetails.accountHolderName}</Text></div>
                                                    <div>Account: <Text strong copyable={!!revealedDetails?.accountNumber}>{revealedDetails?.accountNumber || selectedRequest.refundDetails.accountNumber}</Text></div>
                                                    <div>IFSC: <Text strong copyable={!!revealedDetails?.ifscCode}>{revealedDetails?.ifscCode || selectedRequest.refundDetails.ifscCode}</Text></div>
                                                </div>
                                                {!revealedDetails?.accountNumber && (
                                                    <Button 
                                                        size="small" 
                                                        type="link" 
                                                        onClick={() => handleRevealDetails(selectedRequest)} 
                                                        loading={revealLoading}
                                                        style={{ padding: 0, height: 'auto', marginTop: 4, fontSize: 11 }}
                                                    >
                                                        👁 Reveal Unmasked Bank Details
                                                    </Button>
                                                )}
                                            </div>
                                        )}
                                    </Descriptions.Item>
                                )}
                            </Descriptions>

                            <Title level={5} style={{ fontSize: 16, marginBottom: 12 }}>Returned Items</Title>

                            {(selectedRequest.returnItems?.length ? selectedRequest.returnItems : (selectedRequest.items || [])).map((item, idx) => (
                                <div key={idx} style={{
                                    marginBottom: 8,
                                    background: '#ffffff',
                                    border: '1px solid #e5e7eb',
                                    borderRadius: 12,
                                    padding: '10px 12px',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.02)'
                                }}>
                                    {/* Item Header */}
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                                        <Avatar src={getImageUrl(item.productImage || item.product?.imageUrl)} size={36} shape="square" style={{ borderRadius: 8, border: '1px solid rgba(0,0,0,0.05)' }} />
                                        <div>
                                            <Text strong style={{ fontSize: 13, color: '#111827' }}>{item.productName || item.name}</Text>
                                            <div style={{ color: '#6b7280', fontSize: 11, fontWeight: 500, marginTop: 2 }}>Qty: {item.returnQty || item.quantity} <span style={{ color: '#e5e7eb', margin: '0 6px' }}>|</span> <span style={{ color: '#ff8110', fontWeight: 600 }}>₹{(item.price || 0) * (item.returnQty || item.quantity || 1)}</span></div>
                                        </div>
                                    </div>

                                    {/* Item-level Return Reason */}
                                    {item.returnReason && (
                                        <div style={{ marginBottom: 8, padding: '6px 10px', background: '#fafafa', borderRadius: 8, borderLeft: '3px solid #ff8110' }}>
                                            <Text type="secondary" style={{ fontSize: 10, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Reason: </Text>
                                            <Text style={{ fontSize: 12, color: '#1f2937', fontWeight: 500 }}>{item.returnReason}</Text>
                                            {item.description && (
                                                <div style={{ marginTop: 2, color: '#6b7280', fontSize: 11 }}>{item.description}</div>
                                            )}
                                        </div>
                                    )}

                                    {/* Item-level Evidence Images */}
                                    {item.images && item.images.length > 0 && (
                                        <div>
                                            <Text type="secondary" style={{ display: 'block', fontSize: 10, fontWeight: 600, marginBottom: 8, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Evidence ({item.images.length})</Text>
                                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                                {item.images.map((img, imgIdx) => {
                                                    let imgSrc = typeof img === 'string' ? img : (img?.url || img?.uri || '');
                                                    imgSrc = imgSrc.replace(/\\/g, '/');
                                                    if (imgSrc.includes('uploads/')) {
                                                        imgSrc = '/uploads/' + imgSrc.split('uploads/').pop();
                                                    }

                                                    const localUrl = getImageUrl(imgSrc);
                                                    const fallbackUrl = localUrl?.replace('http://localhost:4000', 'https://api.nearzon.com');
                                                    const imageKey = `${idx}_${imgIdx}`;
                                                    const currentSrc = failedImages[imageKey] ? fallbackUrl : localUrl;

                                                    return (
                                                        <Image
                                                            key={imgIdx}
                                                            src={currentSrc}
                                                            width={72}
                                                            height={72}
                                                            style={{ borderRadius: 8, objectFit: 'cover', border: '1px solid rgba(0,0,0,0.08)', display: 'block' }}
                                                            onError={(e) => {
                                                                if (!failedImages[imageKey]) {
                                                                    setFailedImages(prev => ({ ...prev, [imageKey]: true }));
                                                                } else {
                                                                    // Both local and fallback failed — show placeholder
                                                                    e.target.src = `https://placehold.co/72x72?text=No+Img`;
                                                                    e.target.onerror = null;
                                                                }
                                                            }}
                                                            fallback="https://placehold.co/72x72?text=No+Img"
                                                            preview={{
                                                                mask: <span style={{ fontSize: 10 }}>View</span>
                                                            }}
                                                        />
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ))}

                            {selectedRequest.sellerComment && !['REQUESTED', 'RETURN_REQUESTED', 'RETURN_PENDING'].includes(selectedRequest?.status?.toUpperCase()) && (
                                <div style={{ marginTop: 12, padding: '12px', background: '#eef2ff', borderRadius: '8px', border: '1px solid #c7d2fe' }}>
                                    <Text strong style={{ fontSize: 12, color: '#4338ca' }}>Admin/Seller Note:</Text>
                                    <div style={{ fontSize: 13, color: '#312e81', marginTop: 4 }}>{selectedRequest.sellerComment}</div>
                                </div>
                            )}
                            {selectedRequest.rejectionReason && (
                                <div style={{ marginTop: 12, padding: '12px', background: '#fef2f2', borderRadius: '8px', border: '1px solid #fecaca' }}>
                                    <Text strong style={{ fontSize: 12, color: '#b91c1c' }}>Rejection Details ({selectedRequest.rejectionStage || 'Unknown Stage'}):</Text>
                                    <div style={{ fontSize: 13, color: '#991b1b', marginTop: 4 }}>{selectedRequest.rejectionReason}</div>
                                    {selectedRequest.rejectionDate && (
                                        <div style={{ fontSize: 11, color: '#dc2626', marginTop: 6 }}>{dayjs(selectedRequest.rejectionDate).format('DD MMM YYYY • hh:mm A')}</div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Premium Footer */}
                        <div style={{ padding: '0 16px 16px', background: '#f8f9fc' }}>
                            {/* UTR Input Card */}
                            {['WAITING_FOR_ADMIN_REFUND', 'REFUND_PENDING', 'REFUND_PROCESSING'].includes(selectedRequest?.status?.toUpperCase()) && 
                             ['UPI', 'BANK', 'MANUAL_TRANSFER'].includes(selectedRequest?.refundMethod?.toUpperCase()) && (
                                <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '12px 14px', marginBottom: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}>
                                    <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: '#374151', marginBottom: '6px' }}>
                                        UTR / Transaction Reference ID <span style={{ color: 'red' }}>*</span>
                                    </label>
                                    <Input
                                        placeholder="Enter manual bank transfer UTR or UPI reference ID"
                                        value={utrInput}
                                        onChange={(e) => setUtrInput(e.target.value)}
                                        style={{ borderRadius: '6px' }}
                                    />
                                </div>
                            )}
                            {/* Summary Strip */}
                            <div style={{
                                background: '#fff', border: '1px solid #e5e7eb', borderRadius: '10px',
                                padding: '10px 14px', marginBottom: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
                                display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
                                    <div>
                                        <div style={{ fontSize: '10px', color: '#6b7280', fontWeight: 600, textTransform: 'uppercase' }}>Items</div>
                                        <div style={{ fontSize: '16px', fontWeight: 700, color: '#111827' }}>{(selectedRequest.returnItems?.length ? selectedRequest.returnItems : (selectedRequest.items || [])).length}</div>
                                    </div>
                                    <div style={{ width: '1px', height: '32px', background: '#e5e7eb' }} />
                                    <div>
                                        <div style={{ fontSize: '10px', color: '#6b7280', fontWeight: 600, textTransform: 'uppercase' }}>Total Refund</div>
                                        <div style={{ fontSize: '16px', fontWeight: 700, color: '#16a34a' }}>₹{selectedRequest.refundAmount}</div>
                                    </div>
                                </div>
                                <div style={{ width: '34px', height: '34px', borderRadius: '8px', background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px' }}>
                                    💳
                                </div>
                            </div>

                            {/* Actions */}
                            <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
                                <Button
                                    onClick={() => setIsModalOpen(false)}
                                    style={{ height: '36px', borderRadius: '8px', fontWeight: 600, width: '100px' }}
                                >
                                    Close
                                </Button>
                                {['REQUESTED', 'RETURN_REQUESTED', 'RETURN_PENDING'].includes(selectedRequest?.status?.toUpperCase()) && (
                                    <>
                                        <Button
                                            danger
                                            onClick={() => confirmAction('RETURN_REJECTED_BY_ADMIN')}
                                            loading={actionLoading}
                                            style={{ height: '36px', borderRadius: '8px', fontWeight: 600, width: '120px' }}
                                        >
                                            Reject (Override)
                                        </Button>
                                        <Button
                                            type="primary"
                                            onClick={() => confirmAction('RETURN_APPROVED_BY_ADMIN')}
                                            loading={actionLoading}
                                            style={{ background: '#ff8110', borderColor: '#ff8110', height: '36px', borderRadius: '8px', fontWeight: 600, width: '130px' }}
                                        >
                                            ✓ Approve
                                        </Button>
                                    </>
                                )}
                                {['APPROVED', 'RETURN_APPROVED_BY_ADMIN'].includes(selectedRequest?.status?.toUpperCase()) && (
                                    <Button
                                        type="primary"
                                        onClick={() => handleUpdateStatus('RETURN_PICKUP_ASSIGNED')}
                                        loading={actionLoading}
                                        style={{ background: '#ff8110', borderColor: '#ff8110', height: '36px', borderRadius: '8px', fontWeight: 600, width: '140px' }}
                                    >
                                        ✓ Confirm Pickup
                                    </Button>
                                )}
                                {['PICKED_UP', 'RETURN_PICKED_UP', 'RETURN_STAFF_ACCEPTED'].includes(selectedRequest?.status?.toUpperCase()) && (
                                    <div style={{ display: 'flex', alignItems: 'center', background: '#fffbeb', padding: '0 16px', borderRadius: '8px', border: '1px solid #fef3c7', height: '36px', color: '#d97706', fontWeight: 600, fontSize: 13 }}>
                                        ⏳ Waiting for Delivery...
                                    </div>
                                )}
                                {['RETURN_DELIVERED_TO_SELLER'].includes(selectedRequest?.status?.toUpperCase()) && (
                                    <div style={{ display: 'flex', alignItems: 'center', background: '#e0e7ff', padding: '0 16px', borderRadius: '8px', border: '1px solid #c7d2fe', height: '36px', color: '#4338ca', fontWeight: 600, fontSize: 13 }}>
                                        ⏳ Waiting for Seller to Verify Receipt...
                                    </div>
                                )}
                                {['WAITING_FOR_ADMIN_REFUND'].includes(selectedRequest?.status?.toUpperCase()) && (
                                    <Button
                                        type="primary"
                                        style={{ backgroundColor: '#16a34a', borderColor: '#16a34a', height: '36px', borderRadius: '8px', fontWeight: 600, width: '140px' }}
                                        onClick={handleProcessRefund}
                                        loading={actionLoading}
                                    >
                                        ✓ Finalize Refund
                                    </Button>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </Modal>

            {/* Declarative Confirmation Modal */}
            <Modal
                title={confirmActionStatus.includes('APPROVED') ? "Approve Return Request" : "Reject Return Request"}
                open={isConfirmOpen}
                onCancel={() => setIsConfirmOpen(false)}
                zIndex={1050}
                centered
                okText={confirmActionStatus.includes('APPROVED') ? "Approve" : "Reject"}
                okButtonProps={{ danger: !confirmActionStatus.includes('APPROVED'), loading: actionLoading }}
                onOk={handleConfirmOk}
            >
                <div style={{ marginTop: 16 }}>
                    <Text strong style={{ display: 'block', marginBottom: 8 }}>
                        {confirmActionStatus.includes('APPROVED') ? "Approval Note:" : "Rejection Reason (Required):"}
                    </Text>
                    <Input.TextArea
                        value={confirmNote}
                        onChange={(e) => setConfirmNote(e.target.value)}
                        rows={3}
                        placeholder={confirmActionStatus.includes('APPROVED') ? "Enter approval notes..." : "Enter reason for rejection..."}
                    />
                </div>
            </Modal>
        </div>
    );
};

export default AdminReturns;
